/**
 * @fileOverview Displays a search box that triggers an onChange method when
 * a new search is triggered.
 */
import React, { useState, useEffect } from 'react'
import injectStyles from 'react-jss'
import classNames from 'classnames'
import ReactTags from 'react-tag-autocomplete'
import { generateUniqueId } from './../../Utils/Strings/index'
import brandTheme from '../../Assets/Styles/brandTheme'
import StyledIconButton from '../Form/StyledIconButton'
import SearchIcon from '@material-ui/icons/Search'
const { colors } = brandTheme()
const styles = {
  container: {
    position: 'relative',
    display: 'flex',
    alignItems: 'flex-end',
  },
  searchIconContainer: {
    background: colors.primary,
    width: 40,
    height: 42,
    right: '3px',
    borderRadius: '0 4px 4px 0 !important',
    cursor: 'pointer',
    padding: 10,
  },
  searchIcon: {
    fill: '#FFFFFF',
    width: 18,
    transform: 'rotate(90deg)',
  },
  selectedTagsContainer: {
    margin: '10px 0',
  },
  selectedTag: {
    color: 'white',
    margin: '0px 6px 6px 0px',
    padding: '6px 8px',
    borderRadius: 4,
    background: 'rgb(127, 143, 164)',
    borderWidth: 'initial',
    borderStyle: 'none',
    borderColor: 'initial',
    borderImage: 'initial',
    '&:after': {
      'content': '"✕"',
      color: 'rgb(255, 255, 255)',
      marginLeft: 8,
    },
  },
  suggestions: {
    position: 'absolute',
    top: '100%',
    left: 0,
    width: '100%',
    zIndex: 10,
    '@global': {
      'ul': {
        margin: '4px -1px',
        padding: 0,
        listStyle: 'none',
        background: 'white',
        border: '1px solid #d1d1d1',
        borderRadius: 2,
        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)',
      },
      'li': {
        borderBottom: '1px solid #ddd',
        padding: '6px 8px',
      },
      'li mark': {
        textDecoration: 'underline',
        background: 'none',
        fontWeight: 600,
      },
      'li:hover': {
        cursor: 'pointer',
        background: '#eee',
      },
      'li.is-active': {
        background: '#b7cfe0',
      },
      'li.is-disabled': {
        opacity: 0.5,
        cursor: 'auto',
      },
    },
  },
  reactTags: {
    '@global': {
      'input': {
        padding: '0 60px 0 20px',
        height: 40,
        //background: '#F8F8F8',
        borderRadius: 4,
        border: 'none',
        boxSizing: 'content-box',
        minWidth: 200,
      },
    },
  },
}

/**
 * @typedef SearchBoxProps
 * @property {string} className Style the container div
 * @property {string} inputClassName Style the input field
 * @property {Function} onChange Fired when a search is triggerd, will pass the
 * new search text as the first argument.
 * @property {string} value The current search text
 *
 * @param {SearchBoxProps} props
 */
const SearchBox = props => {
  const {
    classes,
    className,
    inputClassName,
    suggestions = [],
    tags = [],
    onAddTag,
    onRemoveTag,
    placeholder = '',
  } = props
  var [localSearchText, setLocalSearchText] = useState(props.value)
  useEffect(() => {
    setLocalSearchText(props.value)
  }, [props.value])
  const onSearchTap = () => {
    props.onChange(localSearchText)
    if (suggestions.includes(localSearchText)) {
      onAddTag(localSearchText)
    }
  }

  var handleInputChange = str => {
    setLocalSearchText(str)
    return props.handleInputChange ? props.handleInputChange(str) : undefined
  }

  // Fired on key down in the input field
  var onKeyDown = e => {
    // Hacky as hell but spent an hour fighting the order the handler
    // functions fire in and it always seems to fire this one before the
    // ReactTags key down handler even though this one is seemingly always
    // added last
    setTimeout(() => {
      if (cancelSearch == false && e.keyCode == 13) {
        onSearchTap()
      }
    }, 4) // SetTimeout the very minimum to make this fire after the ReactTags
    // handler
  }

  var cancelSearch = false
  var preCheck = () => {
    // Fired before the ReactTags keyDown event so it resets the cancel prop
    // this will get set to true if a tag was selected and thus cancel search
    cancelSearch = false
  }

  // To make hitting ENTER trigger a search
  var [containerId] = useState('a' + generateUniqueId())
  useEffect(() => {
    // Meh hacky hacky working around third party libs
    document
      .querySelector(`#${containerId} input`)
      .addEventListener('keydown', preCheck, true)
    document
      .querySelector(`#${containerId} input`)
      .addEventListener('keydown', onKeyDown)
    return () => {
      document
        .querySelector(`#${containerId} input`)
        .removeEventListener('keydown', preCheck, true)
      document
        .querySelector(`#${containerId} input`)
        .removeEventListener('keydown', onKeyDown)
    }
  })

  return (
    <div className={classNames(classes.container, className)} id={containerId}>
      <ReactTags
        classNames={{
          root: classes.reactTags,
          selected: classes.selectedTagsContainer,
          selectedTag: classes.selectedTag,
          suggestions: classes.suggestions,
          searchInput: classNames(classes.searchInput, inputClassName),
        }}
        tags={tags.map(s => ({ id: s, name: s }))}
        suggestions={suggestions.map(s => ({ id: s, name: s }))}
        handleDelete={index => {
          // I've seen -1 return here, must be a bug in the react-tag-autocomplete
          // package
          if (index == -1) return
          onRemoveTag(tags[index])
        }}
        handleAddition={item => {
          cancelSearch = true
          onAddTag(item.id)
        }}
        handleInputChange={handleInputChange}
        placeholder={placeholder}
        inputAttributes={{ onKeyDown }}
        clearInputOnDelete={false}
      />

      <StyledIconButton
        className={classes.searchIconContainer}
        onClick={onSearchTap}
      >
        <SearchIcon />
      </StyledIconButton>
    </div>
  )
}

const StylesSearchBox = injectStyles(styles)(SearchBox)

export default StylesSearchBox



// WEBPACK FOOTER //
// ./src/Components/FilterAndPaginationComponent/SearchBox.js